/**
 * LeadSearchAutocomplete Component
 * Searchable dropdown for selecting leads with hint display
 */

import React, { useState, useCallback } from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Chip,
} from "@mui/material";
import { Description, LocationOn } from "@mui/icons-material";
import { searchLeads } from "../../features/leads/services/leadSearchService.js";

/**
 * LeadSearchAutocomplete Component
 * @param {Object} props
 * @param {Object|null} props.value - Selected lead object
 * @param {Function} props.onChange - Callback when lead is selected/changed
 * @param {string} [props.label] - Label for the field
 * @param {boolean} [props.disabled] - Whether field is disabled
 * @param {boolean} [props.showQuotedStatus] - Show quoted status indicator for leads
 */
const LeadSearchAutocomplete = ({
  value,
  onChange,
  label = "Search Lead (Optional)",
  disabled = false,
  showQuotedStatus = false,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Debounced search function
  const search = useCallback(
    async (searchTerm) => {
      if (!searchTerm || searchTerm.trim().length < 2) {
        setOptions([]);
        return;
      }

      setLoading(true);
      try {
        const results = await searchLeads(searchTerm, {
          includeQuotedStatus: showQuotedStatus,
        });
        setOptions(results);
      } catch (err) {
        console.error("Error searching leads:", err);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [showQuotedStatus]
  );

  // Handle input change with debounce
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (inputValue) {
        search(inputValue);
      } else {
        setOptions([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [inputValue, search]);

  // Format lead hint: "from → to, date"
  const getLeadHint = (lead) => {
    const parts = [];
    if (lead.moving_from && lead.moving_to) {
      parts.push(`${lead.moving_from} → ${lead.moving_to}`);
    } else if (lead.moving_from) {
      parts.push(`From: ${lead.moving_from}`);
    } else if (lead.moving_to) {
      parts.push(`To: ${lead.moving_to}`);
    }

    if (lead.move_date) {
      parts.push(lead.move_date);
    }

    return parts.length > 0 ? parts.join(", ") : "No location/date info";
  };

  return (
    <Autocomplete
      disabled={disabled}
      value={value}
      onChange={(event, newValue) => {
        onChange(newValue);
      }}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      options={options}
      getOptionLabel={(option) => {
        if (!option) return "";
        return option.name || option.phone || "Unknown Lead";
      }}
      isOptionEqualToValue={(option, value) => {
        return option?.id === value?.id || option?._id === value?._id;
      }}
      loading={loading}
      filterOptions={(x) => x} // Disable client-side filtering, we do server-side
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder="Search by name, phone, email, or reference..."
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        const hint = getLeadHint(option);

        return (
          <Box component="li" {...props} sx={{ py: 1.5, px: 2 }}>
            <Box sx={{ width: "100%" }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
              >
                <Description fontSize="small" color="action" />
                <Typography variant="body2" fontWeight="medium">
                  {option.name || "Unknown"}
                </Typography>
                {option.phone && (
                  <Chip
                    label={option.phone}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: "0.7rem" }}
                  />
                )}
                {option.referenceNumber && (
                  <Chip
                    label={option.referenceNumber}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: "0.7rem" }}
                  />
                )}
                {option.hasQuote && (
                  <Chip
                    label="Quoted"
                    size="small"
                    color="warning"
                    sx={{
                      height: 20,
                      fontSize: "0.7rem",
                      fontWeight: "medium",
                    }}
                  />
                )}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  ml: 3.5,
                  flexWrap: "wrap",
                }}
              >
                <LocationOn
                  fontSize="small"
                  color="action"
                  sx={{ fontSize: 14 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {hint}
                </Typography>
              </Box>
              {option.contactInfo?.email && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 3.5, display: "block" }}
                >
                  {option.contactInfo.email}
                </Typography>
              )}
            </Box>
          </Box>
        );
      }}
      noOptionsText={
        inputValue.trim().length < 2
          ? "Type at least 2 characters to search"
          : "No leads found"
      }
    />
  );
};

export default LeadSearchAutocomplete;
